// Paste Formulir Pelanggan (Offline / Shopee / Tokopedia): tempel → Baca Formulir → koreksi → Konfirmasi Pembacaan.
// Konfirmasi BUKAN ACC pelanggan: tidak mengubah status FIX, tahap produksi, file mockup, maupun stok.
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { ClipboardPaste, X } from "lucide-react";
import { toast } from "sonner";
import { BlockEditor, NeedTable, Removals, ResultView, Warnings } from "./design_form_parts";
import "./design_form.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/design-forms`;
const CHAT_CHECK_ON = false; // ditunda Owner (hemat biaya AI) — tombol Simpan Teks & Periksa disembunyikan
const errMsg = (e, d) => e?.response?.data?.detail || d;

function useCurrent(channel, orderNumber, jobId) {
  const [cur, setCur] = useState(null);
  const load = useCallback(() => axios.get(`${API}/current`, { params: { channel, order_number: orderNumber, job_id: jobId || "" }, withCredentials: true })
    .then(r => setCur(r.data)).catch(() => setCur(null)), [channel, orderNumber, jobId]);
  useEffect(() => { load(); }, [load]);
  return [cur, load];
}

function FormModal({ channel, orderNumber, jobId, products, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [pids, setPids] = useState(() => (products || []).map(p => p.id));
  const [mode, setMode] = useState(null);
  const [blocks, setBlocks] = useState(null);
  const [res, setRes] = useState(null);
  const [okRemove, setOkRemove] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const tmr = useRef(null);
  const scope = { channel, order_number: orderNumber, job_id: jobId || "", product_ids: channel === "offline" ? pids : [] };
  const run = async (body) => {
    try { const r = await axios.post(`${API}/preview`, { ...scope, ...body }, { withCredentials: true }); setRes(r.data); return r.data; }
    catch (e) { toast.error(errMsg(e, "Gagal membaca formulir")); return null; }
  };
  const read = async () => {
    setBusy(true); const r = await run({ text }); setBusy(false);
    if (r) { setBlocks(r.parsed.blocks); setMode(r.mode); setOkRemove(false); }
  };
  useEffect(() => {
    if (!blocks) return undefined;
    clearTimeout(tmr.current);
    tmr.current = setTimeout(() => run({ text, blocks, mode }), 350);
    return () => clearTimeout(tmr.current);
  }, [blocks, mode, pids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const red = (res?.warnings || []).some(w => w.level === "merah");
  const rem = [...(res?.removals || []), ...(res?.cleared || [])];
  const saveChat = async () => {
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/form-mockup-check/job/${jobId}/chat-text`, { text }, { withCredentials: true });
      toast.success("Teks chat disimpan — AI memeriksa mockup"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e, "Gagal menyimpan teks")); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/confirm`, { ...scope, text, blocks, mode, confirm_removals: okRemove, reason }, { withCredentials: true });
      toast.success("Pembacaan formulir dikonfirmasi"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e, "Gagal mengonfirmasi")); } finally { setBusy(false); }
  };
  return <div className="dfm-overlay" onClick={onClose} data-testid="dfm-modal">
    <div className="dfm-panel" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Paste Formulir Pelanggan">
      <header><b>Paste Formulir · {orderNumber}</b><button type="button" onClick={onClose} aria-label="Tutup" data-testid="dfm-close"><X size={16}/></button></header>
      {channel === "offline" && (products || []).length > 1 && <div className="dfm-prods" data-testid="dfm-products">
        <span>Formulir untuk produk:</span>
        {products.map(p => <label key={p.id}><input type="checkbox" checked={pids.includes(p.id)} data-testid={`dfm-prod-${p.id}`}
          onChange={e => setPids(v => e.target.checked ? [...v, p.id] : v.filter(x => x !== p.id))}/> {p.custom_name || p.name}</label>)}
      </div>}
      <textarea className="dfm-text" rows={6} value={text} onChange={e => setText(e.target.value)} data-testid="dfm-text"
        placeholder={"Tempel seluruh isi chat formulir di sini…\nWarna Baju =\nSize =\nBelakang =\n…"}/>
      <button type="button" className="dfm-main" disabled={busy || !text.trim() || (channel === "offline" && !pids.length)} onClick={read} data-testid="dfm-read">Baca Formulir</button>
      {CHAT_CHECK_ON && channel !== "offline" && jobId && <button type="button" className="dfm-main" disabled={busy || text.trim().length < 3} onClick={saveChat}
        data-testid="dfm-save-chat">Simpan Teks &amp; Periksa Mockup</button>}
      {CHAT_CHECK_ON && channel !== "offline" && jobId && <small className="dfm-muted">Cukup Simpan Teks: AI langsung membandingkan teks asli dengan mockup. "Baca Formulir" hanya cadangan.</small>}
      {res && blocks && <>
        <div className="dfm-mode" data-testid="dfm-mode">
          {res.previous ? <>Versi aktif v{res.previous.version}. Jenis kiriman:
            <label><input type="radio" checked={mode === "parsial"} onChange={() => setMode("parsial")} data-testid="dfm-mode-parsial"/> Revisi parsial (kolom kosong tidak mengubah)</label>
            <label><input type="radio" checked={mode === "lengkap"} onChange={() => setMode("lengkap")} data-testid="dfm-mode-lengkap"/> Formulir lengkap (menggantikan semua)</label>
            {res.mode_guess !== mode && <small>Terdeteksi: {res.mode_guess === "lengkap" ? "formulir lengkap" : "revisi parsial"}</small>}</>
            : <>Formulir pertama untuk pesanan ini.</>}
        </div>
        {res.parsed.unrecognized?.length > 0 && <p className="dfm-muted" data-testid="dfm-unrec">Tidak terbaca: {res.parsed.unrecognized.join(" · ")}</p>}
        <div className="dfm-grid">
          <div>{blocks.map((b, i) => <BlockEditor key={i} idx={i} block={b} onChange={nb => setBlocks(bs => bs.map((x, j) => j === i ? nb : x))}/>)}</div>
          <div><h4>Hasil akhir</h4><ResultView final={res.final}/><h4>Data pesanan</h4><NeedTable lines={res.need.lines}/><Warnings items={res.warnings}/></div>
        </div>
        <Removals items={rem} checked={okRemove} onCheck={setOkRemove}/>
        {red && <input className="dfm-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="Alasan konfirmasi meski berbeda dengan pesanan" data-testid="dfm-reason"/>}
        <footer><small>Konfirmasi pembacaan ≠ ACC pelanggan. Status FIX & tahap produksi tidak berubah.</small>
          <button type="button" className="dfm-main" data-testid="dfm-confirm" onClick={save}
            disabled={busy || (rem.length > 0 && !okRemove) || (red && reason.trim().length < 3)}>Konfirmasi Pembacaan</button></footer>
      </>}
    </div>
  </div>;
}

export function DesignFormButton({ channel, orderNumber, jobId = "", products = [], onSaved }) {
  const [cur, reload] = useCurrent(channel, orderNumber, jobId);
  const load = () => { reload(); if (onSaved) onSaved(); };
  const [open, setOpen] = useState(false);
  if (!cur) return null;
  const pidSet = new Set(products.map(p => p.id));
  const act = cur.active.filter(v => channel !== "offline" || (v.active_product_ids || []).some(id => pidSet.has(id)));
  const last = act[0];
  const flag = last && (last.warnings || []).some(w => w.level === "merah");
  const label = last ? `Formulir v${last.version}${act.length > 1 ? ` +${act.length - 1}` : ""}` : "Belum ada formulir";
  const tid = `dfm-btn-${orderNumber}${jobId ? `-${jobId}` : ""}`;
  return <span className="dfm-wrap" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
    <span className={`dfm-badge${last ? " is-ok" : ""}${flag ? " is-flag" : ""}`} title={last ? `Dikonfirmasi ${last.confirmed_by}` : undefined} data-testid={`${tid}-status`}>{label}{flag ? " · periksa" : ""}</span>
    {cur.can_edit && <button type="button" className="dfm-open" onClick={() => setOpen(true)} data-testid={tid}><ClipboardPaste size={12}/> Paste Formulir</button>}
    {open && <FormModal channel={channel} orderNumber={orderNumber} jobId={jobId} products={products} onClose={() => setOpen(false)} onSaved={load}/>}
  </span>;
}
